import type { SavedViewFilters, SavedViewsListResponse } from '#services/api/saved-views.ts';

import { z } from 'zod';

import { getDateRangeSearchParamsSchema } from '#utils/dates.ts';
import { allProvidersToken } from '#utils/provider-filter.ts';
import { BASE_APPOINTMENT_TYPES } from './Filters/AppointmentTypes.utils.ts';

const idsArraySchema = z
  .array(z.string())
  .transform((value) => value.map(Number).filter((value) => value > 0));

const providerFilterSchema = z
  .array(z.string())
  .transform((values) =>
    values.includes(allProvidersToken)
      ? allProvidersToken
      : values.map(Number).filter((value) => value > 0)
  );

const formPrioritySchema = z.union([
  z.literal('all'),
  z.literal('required'),
  z.literal('optional'),
]);

const homeTabSchema = z.union([
  z.literal('scheduling'),
  z.literal('forms'),
  z.literal('coordinate'),
  z.literal('transferQueue'),
]);

const searchParamsSchema = z.object({
  tab: homeTabSchema.catch('scheduling'),
  view: z.coerce.number().positive().optional().catch(undefined),
  providerIds: providerFilterSchema,
  appointmentTypeIds: idsArraySchema,
  addressIds: idsArraySchema,
  bookingChannelIds: z
    .array(z.union([z.literal('staff'), z.literal('pss'), z.literal('import')]))
    .catch([]),
  baseAppointmentTypes: z.array(z.enum(BASE_APPOINTMENT_TYPES)).catch([]),
  assessmentTypeIds: idsArraySchema,
  formsPriority: formPrioritySchema.catch('all'),
  appointmentId: z.coerce.number().positive().optional().catch(undefined),
  appointmentStatusIds: idsArraySchema,
  queueState: z.enum(['all', 'current', 'completed']).catch('all'),
  patientDisconnected: z.enum(['all', 'yes', 'no']).catch('all'),
});

export type HomeTab = z.infer<typeof searchParamsSchema>['tab'];

/**
 * Parses the search params from the URL. Since the home route renders the
 * appointments and forms drilldowns as nested routes, we need to carry the
 * "base" search params into those routes in order for the home page to
 * maintain its state while the dialogs are displayed.
 */
export function getParsedSearchParams(searchParams: URLSearchParams) {
  const result = searchParamsSchema.parse({
    tab: searchParams.get('tab'),
    view: searchParams.get('view'),
    providerIds: searchParams.getAll('pId'),
    appointmentTypeIds: searchParams.getAll('atId'),
    addressIds: searchParams.getAll('aId'),
    bookingChannelIds: searchParams.getAll('bcId'),
    baseAppointmentTypes: searchParams.getAll('bat'),
    assessmentTypeIds: searchParams.getAll('astId'),
    formsPriority: searchParams.get('fp'),
    appointmentId: searchParams.get('qId'),
    appointmentStatusIds: searchParams.getAll('qsId'),
    queueState: searchParams.get('qState'),
    patientDisconnected: searchParams.get('qDisconnected'),
  });

  const dateRangeResult = getDateRangeSearchParamsSchema().parse({
    dp: searchParams.get('dp'),
    from: searchParams.get('from'),
    to: searchParams.get('to'),
  });

  return { ...result, ...dateRangeResult };
}

/**
 * Maps from URL search param keys to the `filters` object keys stored by the
 * API. Only filter-related params are included — `tab` and `view` are excluded
 * because `tabType` lives on the saved view record itself.
 */
const filterParamMap = {
  dp: 'datePreset',
  from: 'from',
  to: 'to',
  pId: 'providerIds',
  atId: 'appointmentTypeIds',
  aId: 'addressIds',
  bcId: 'bookingChannelIds',
  bat: 'baseAppointmentTypes',
  astId: 'assessmentTypeIds',
  fp: 'formsPriority',
  qId: 'appointmentId',
  qsId: 'appointmentStatusIds',
  qState: 'queueState',
  qDisconnected: 'patientDisconnected',
} as const;

const knownFilterKeys = new Set<string>(Object.values(filterParamMap));

type FilterParam = keyof typeof filterParamMap;
type FilterKey = (typeof filterParamMap)[FilterParam];

/** The non-date filter keys — the ones a tab's filter policy enumerates. */
type TabFilterKey = Exclude<FilterKey, 'datePreset' | 'from' | 'to'>;

export const paramByFilterKey = Object.fromEntries(
  Object.entries(filterParamMap).map(([param, key]) => [key, param])
) as Record<FilterKey, FilterParam>;

/**
 * Which filters belong to each tab — the single statement of Signal's per-tab filter policy.
 * A key listed under two tabs is shared between them (its URL param carries across on tab
 * switches); the date range is its own axis because the Coordinate tab has no date filter.
 * Everything else — parsing, serializing, entry wipes — is mechanism deriving from this table.
 */
export const tabFilterPolicy = {
  scheduling: {
    dates: true,
    keys: [
      'providerIds',
      'appointmentTypeIds',
      'addressIds',
      'bookingChannelIds',
      'baseAppointmentTypes',
    ],
  },
  forms: {
    dates: true,
    keys: ['providerIds', 'assessmentTypeIds', 'formsPriority'],
  },
  coordinate: {
    dates: false,
    keys: ['providerIds', 'appointmentTypeIds', 'baseAppointmentTypes'],
  },
  transferQueue: {
    dates: true,
    keys: [
      'providerIds',
      'appointmentTypeIds',
      'addressIds',
      'appointmentId',
      'appointmentStatusIds',
      'queueState',
      'patientDisconnected',
    ],
  },
} as const satisfies Record<HomeTab, { dates: boolean; keys: readonly TabFilterKey[] }>;

type ParsedSearchParams = ReturnType<typeof getParsedSearchParams>;

// How each filter key serializes from parsed URL state into a saved view's filters — `undefined`
// means "inactive, omit". Ids travel as strings per the saved-views wire shape.
const filterKeySerializers: {
  [K in TabFilterKey]: (parsed: ParsedSearchParams) => SavedViewFilters[K] | undefined;
} = {
  providerIds: (parsed) =>
    parsed.providerIds === allProvidersToken ? allProvidersToken : toIdStrings(parsed.providerIds),
  appointmentTypeIds: (parsed) => toIdStrings(parsed.appointmentTypeIds),
  addressIds: (parsed) => toIdStrings(parsed.addressIds),
  bookingChannelIds: (parsed) =>
    parsed.bookingChannelIds.length > 0 ? parsed.bookingChannelIds : undefined,
  baseAppointmentTypes: (parsed) =>
    parsed.baseAppointmentTypes.length > 0 ? parsed.baseAppointmentTypes : undefined,
  assessmentTypeIds: (parsed) => toIdStrings(parsed.assessmentTypeIds),
  formsPriority: (parsed) => (parsed.formsPriority === 'all' ? undefined : parsed.formsPriority),
  appointmentId: (parsed) => parsed.appointmentId,
  appointmentStatusIds: (parsed) => toIdStrings(parsed.appointmentStatusIds),
  queueState: (parsed) => (parsed.queueState === 'all' ? undefined : parsed.queueState),
  patientDisconnected: (parsed) =>
    parsed.patientDisconnected === 'all' ? undefined : parsed.patientDisconnected,
};

function toIdStrings(ids: number[]): string[] | undefined {
  return ids.length > 0 ? ids.map(String) : undefined;
}

// A correlated generic write TS can't express through an indexed loop on its own.
function setFilter<K extends TabFilterKey>(
  filters: SavedViewFilters,
  key: K,
  value: SavedViewFilters[K] | undefined
) {
  if (value !== undefined) {
    filters[key] = value;
  }
}

/**
 * Serializes validated filter state into the `filters` object shape expected by
 * the saved views API. Only the active tab's keys (per `tabFilterPolicy`) are
 * included — the other tabs' filters are excluded to prevent stale cross-tab
 * values from being persisted.
 *
 * Date handling (only on tabs whose policy carries dates):
 * - If `dp` (date preset) is present → stores `datePreset` only, omits `from`/`to`
 * - If explicit `from` and/or `to` are present (no `dp`) → stores whichever are set
 * - If neither is present → omits date fields entirely (page default applies on load)
 */
export function serializeFilters(options: {
  parsed: ParsedSearchParams;
  tabType: HomeTab;
}): SavedViewFilters {
  const { parsed, tabType } = options;
  const policy = tabFilterPolicy[tabType];
  const filters: SavedViewFilters = {};

  if (policy.dates) {
    if (parsed.datePreset) {
      filters.datePreset = parsed.datePreset;
    } else {
      filters.from = parsed.fromDateFormat;
      filters.to = parsed.toDateFormat;
    }
  }

  for (const key of policy.keys) {
    setFilter(filters, key, filterKeySerializers[key](parsed));
  }

  return filters;
}

/**
 * Canonical form for filter comparison: no `undefined`s, no empty arrays, arrays and keys
 * sorted. Key order is part of the canonical form because comparison is JSON-based, and stored
 * view filters arrive as server JSON whose key order carries no guarantee.
 */
export function normalizeFilters(filters: SavedViewFilters): SavedViewFilters {
  type SavedFilterValue = Exclude<SavedViewFilters[keyof SavedViewFilters], undefined>;
  const normalizedEntries: Array<[string, SavedFilterValue]> = [];

  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined) {
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        continue;
      }

      normalizedEntries.push([
        key,
        [...value].sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right))),
      ]);
      continue;
    }

    normalizedEntries.push([key, value]);
  }

  return Object.fromEntries(
    normalizedEntries.sort(([left], [right]) => String(left).localeCompare(String(right)))
  ) as SavedViewFilters;
}

/** Compares two filter objects by their canonical form — raw inputs are fine. */
export function areFiltersEqual(left: SavedViewFilters, right: SavedViewFilters) {
  return JSON.stringify(normalizeFilters(left)) === JSON.stringify(normalizeFilters(right));
}

/** Keeps API-owned opaque values while replacing every filter this frontend understands. */
export function mergeOpaqueSavedViewFilters(options: {
  savedFilters: SavedViewFilters;
  serializedFilters: SavedViewFilters;
}): SavedViewFilters {
  const opaqueEntries = Object.entries(options.savedFilters).filter(
    ([key]) => !knownFilterKeys.has(key)
  );

  return {
    ...Object.fromEntries(opaqueEntries),
    ...options.serializedFilters,
  };
}

/**
 * Inverse of `serializeFilters` — takes the `filters` object from a saved view
 * and writes it back into URL search params. Clears existing filter params
 * first to prevent stale values from persisting.
 *
 * Date handling:
 * - If `datePreset` exists → writes `dp`, omits `from`/`to`
 * - If `from` and/or `to` exist (no preset) → writes whichever are set
 * - If neither exists → writes nothing (page default applies)
 */
export function applyFiltersToSearchParams(
  filters: SavedViewFilters,
  searchParams: URLSearchParams
): URLSearchParams {
  const mergedSearchParams = new URLSearchParams(searchParams);

  for (const param of Object.keys(filterParamMap)) {
    mergedSearchParams.delete(param);
  }

  if (filters.datePreset) {
    mergedSearchParams.set('dp', filters.datePreset);
  } else {
    if (filters.from) mergedSearchParams.set('from', filters.from);
    if (filters.to) mergedSearchParams.set('to', filters.to);
  }

  for (const [key, value] of Object.entries(filters)) {
    if (key === 'datePreset' || key === 'from' || key === 'to') continue;

    const param = getFilterParam(key);
    if (!param) continue;

    if (Array.isArray(value)) {
      for (const v of value) {
        const serializedValue = toSearchParamValue(v);
        if (serializedValue !== undefined) {
          mergedSearchParams.append(param, serializedValue);
        }
      }
    } else {
      const serializedValue = toSearchParamValue(value);
      if (serializedValue !== undefined) {
        mergedSearchParams.set(param, serializedValue);
      }
    }
  }

  return mergedSearchParams;
}

function getFilterParam(key: string): FilterParam | undefined {
  return Object.prototype.hasOwnProperty.call(paramByFilterKey, key)
    ? paramByFilterKey[key as FilterKey]
    : undefined;
}

function toSearchParamValue(value: SavedViewFilters[string]): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return undefined;
}

/**
 * Resolves the effective search params from a saved view when one is active.
 * The view is the complete filter source of truth, so other filter params in
 * the URL are ignored. Non-filter params (including `view` itself) are retained.
 *
 * @param searchParams - The raw URL search params from the request
 * @param savedViewsData - All available saved views to look up the view ID
 */
export function getSearchParamsToParse(
  searchParams: URLSearchParams,
  savedViewsData: SavedViewsListResponse
) {
  let paramsToParse = new URLSearchParams(searchParams);

  if (searchParams.has('view')) {
    const viewId = Number(searchParams.get('view'));
    const view = savedViewsData.views.find((v) => v.id === viewId);

    if (view) {
      paramsToParse = applyFiltersToSearchParams(view.filters, searchParams);
      paramsToParse.set('tab', view.tabType);
    } else {
      paramsToParse.delete('view');
    }
  }

  return paramsToParse;
}

export function getReadinessMagicLinkSearchParams(searchParams: URLSearchParams) {
  const cleanedSearchParams = new URLSearchParams();
  // A campaign can address another supported tab via an explicit ?tab= on the token's nextUrl;
  // magic links land on Coordinate otherwise.
  cleanedSearchParams.set('tab', homeTabSchema.catch('coordinate').parse(searchParams.get('tab')));

  for (const [key, value] of searchParams) {
    if (key === 'mt' || key === 'tab') continue;

    cleanedSearchParams.append(key, value);
  }

  return cleanedSearchParams;
}

export function getReadinessMagicLinkSearchParamsFromNextUrl(options: {
  nextUrl: string;
  fallbackSearchParams: URLSearchParams;
}) {
  try {
    return getReadinessMagicLinkSearchParams(
      new URL(options.nextUrl, window.location.origin).searchParams
    );
  } catch {
    return getReadinessMagicLinkSearchParams(options.fallbackSearchParams);
  }
}
